import { useEffect, useRef, useState } from "react";
import { Select } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import uPlot, { type AlignedData, type Options } from "uplot";
import "uplot/dist/uPlot.min.css";
import { api, type SensorSeries } from "../api";

type ViewMode = "total" | "nix" | "ixp";

const VIEW_OPTIONS: { label: string; value: ViewMode }[] = [
  { label: "Total", value: "total" },
  { label: "NIX (Domestic)", value: "nix" },
  { label: "IXP (International)", value: "ixp" },
];

// ── helpers ─────────────────────────────────────────────────────────────────

function fmtBps(v: number | null): string {
  if (v == null || isNaN(v)) return "—";
  const a = Math.abs(v);
  if (a >= 1e9) return (v / 1e9).toFixed(1) + " G";
  if (a >= 1e6) return (v / 1e6).toFixed(1) + " M";
  if (a >= 1e3) return (v / 1e3).toFixed(1) + " k";
  return String(Math.round(v));
}

// Wansight-style palette: dark red for first sensor, then alternating warm tones
const PALETTE = [
  "#a01010", // dark red (Wansight primary)
  "#d04020", // dark orange-red
  "#1060c0", // blue
  "#208040", // green
  "#7030A0", // purple
  "#c08000", // gold
  "#007090", // teal
  "#900060", // magenta
];

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// ── presets ──────────────────────────────────────────────────────────────────

const RANGES: { label: string; value: number }[] = [
  { label: "Last 5 minutes", value: 5 * 60 },
  { label: "Last 10 minutes", value: 10 * 60 },
  { label: "Last 15 minutes", value: 15 * 60 },
  { label: "Last Hour", value: 60 * 60 },
  { label: "Last 3 Hours", value: 3 * 60 * 60 },
  { label: "Last 12 Hours", value: 12 * 60 * 60 },
  { label: "Last Day", value: 24 * 60 * 60 },
  { label: "Last Week", value: 7 * 24 * 60 * 60 },
  { label: "Last Month", value: 30 * 24 * 60 * 60 },
];

const REFRESHES: { label: string; value: number }[] = [
  { label: "off", value: 0 },
  { label: "5 seconds", value: 5_000 },
  { label: "30 seconds", value: 30_000 },
  { label: "1 minute", value: 60_000 },
  { label: "5 minutes", value: 5 * 60_000 },
];

// ── component ────────────────────────────────────────────────────────────────

interface Props {
  seconds?: number;
  refreshMs?: number;
  height?: number;
}

export default function LiveChart({
  seconds: initialSeconds = 600,
  refreshMs: initialRefresh = 5000,
  height = 240,
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const plotRef = useRef<uPlot | null>(null);
  const [empty, setEmpty] = useState(false);
  const [seconds, setSeconds] = useState(initialSeconds);
  const [refreshMs, setRefreshMs] = useState(initialRefresh);
  const [nonce, setNonce] = useState(0);
  const [view, setView] = useState<ViewMode>("total");

  useEffect(() => {
    let cancelled = false;

    // Convert per-sensor series into uPlot AlignedData.
    // Layout: [timestamps, s0_in, s0_out, s1_in, s1_out, ...]
    // out values are negated so they appear below the zero line.
    function toAligned(sensors: SensorSeries[]): AlignedData {
      if (sensors.length === 0) return [[], []];

      // Build a union of all timestamps (they should all be the same grid
      // due to fill(0), but union-merge to be safe).
      const tsSet = new Set<number>();
      for (const s of sensors) for (const p of s.points) tsSet.add(p.t);
      const ts = Array.from(tsSet).sort((a, b) => a - b);

      const data: AlignedData = [ts];
      for (const sensor of sensors) {
        const byT = new Map(sensor.points.map((p) => [p.t, p]));
        const inn: (number | null)[] = [];
        const out: (number | null)[] = [];
        for (const t of ts) {
          const p = byT.get(t);
          inn.push(p ? p.in : null);
          out.push(p ? -p.out : null); // negative → below zero
        }
        data.push(inn, out);
      }
      return data;
    }

    // Build uPlot series config for a list of sensor names.
    function buildSeries(sensors: SensorSeries[]): Options["series"] {
      const base: Options["series"] = [{}]; // x
      sensors.forEach((s, i) => {
        const color = PALETTE[i % PALETTE.length];
        base.push(
          {
            label: `${s.name} in`,
            stroke: color,
            fill: hexToRgba(color, 0.55),
            width: 1,
            points: { show: false },
            value: (_u, v) => fmtBps(v),
          },
          {
            label: `${s.name} out`,
            stroke: color,
            fill: hexToRgba(color, 0.4),
            width: 1,
            points: { show: false },
            value: (_u, v) => fmtBps(v == null ? null : -v),
          }
        );
      });
      return base;
    }

    async function load() {
      try {
        const sensors: SensorSeries[] =
          view === "total"
            ? await api.seriesSensors(seconds)
            : await api.seriesGeo(seconds, view);
        if (cancelled) return;

        const hasData = sensors.some((s) =>
          s.points.some((p) => p.in > 0 || p.out > 0)
        );
        setEmpty(!hasData);

        const data = toAligned(sensors);

        // Rebuild if sensor count changed (need different series config).
        const expectedSeriesCount = sensors.length * 2 + 1;
        if (
          plotRef.current &&
          plotRef.current.series.length !== expectedSeriesCount
        ) {
          plotRef.current.destroy();
          plotRef.current = null;
        }

        if (!plotRef.current && ref.current) {
          const opts: Options = {
            width: ref.current.clientWidth,
            height,
            cursor: { drag: { x: false, y: false } },
            legend: { show: true, live: true },
            scales: {
              x: { time: true },
              y: { auto: true },
            },
            axes: [
              {
                stroke: "#888",
                grid: { stroke: "#eee", width: 1 },
                ticks: { stroke: "#ddd" },
                font: "11px -apple-system, sans-serif",
              },
              {
                stroke: "#888",
                grid: { stroke: "#eee", width: 1 },
                ticks: { stroke: "#ddd" },
                font: "11px -apple-system, sans-serif",
                // size must be wide enough for "150.0 M" (7 chars × ~9px).
                // Without explicit size uPlot auto-sizes from the shortest
                // label ("0") → longer labels get left-clipped ("50.0 M").
                size: 68,
                gap:  5,
                values: (_u, ticks) => ticks.map((v) => fmtBps(Math.abs(v))),
              },
            ],
            series: buildSeries(sensors),
          };
          plotRef.current = new uPlot(opts, data, ref.current);
        } else if (plotRef.current) {
          plotRef.current.setData(data);
        }
      } catch {
        // chart stays blank on error
      }
    }

    load();
    const id =
      refreshMs > 0 ? window.setInterval(load, refreshMs) : undefined;

    function onResize() {
      if (plotRef.current && ref.current) {
        plotRef.current.setSize({ width: ref.current.clientWidth, height });
      }
    }
    window.addEventListener("resize", onResize);

    // ResizeObserver catches container size changes that window resize
    // misses (sidebar toggle, antd layout animations, etc.).
    let ro: ResizeObserver | undefined;
    if (ref.current) {
      ro = new ResizeObserver(onResize);
      ro.observe(ref.current);
    }

    return () => {
      cancelled = true;
      if (id !== undefined) window.clearInterval(id);
      window.removeEventListener("resize", onResize);
      ro?.disconnect();
    };
  }, [seconds, refreshMs, height, nonce, view]);

  // Destroy uPlot only on unmount.
  useEffect(() => {
    return () => {
      if (plotRef.current) {
        plotRef.current.destroy();
        plotRef.current = null;
      }
    };
  }, []);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Controls row — inline in widget body (header is owned by DashboardGrid) */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "3px 8px",
          borderBottom: "1px solid #e0e4ea",
          background: "#fafbfc",
          flexShrink: 0,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <span style={{ fontSize: 10, color: "#667" }}>View</span>
        <Select
          size="small"
          value={view}
          onChange={(v) => {
            setView(v);
            if (plotRef.current) { plotRef.current.destroy(); plotRef.current = null; }
          }}
          options={VIEW_OPTIONS}
          style={{ width: 130 }}
        />
        <span style={{ fontSize: 10, color: "#667" }}>Time Range</span>
        <Select
          size="small"
          value={seconds}
          onChange={(v) => {
            setSeconds(v);
            if (plotRef.current) { plotRef.current.destroy(); plotRef.current = null; }
          }}
          options={RANGES}
          style={{ width: 118 }}
        />
        <span style={{ fontSize: 10, color: "#667" }}>Refresh</span>
        <Select
          size="small"
          value={refreshMs}
          onChange={(v) => setRefreshMs(v)}
          options={REFRESHES}
          style={{ width: 92 }}
          suffixIcon={
            <ReloadOutlined
              onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); setNonce((n) => n + 1); }}
              style={{ cursor: "pointer", color: "#c0392b" }}
            />
          }
        />
      </div>

      {/* Chart area */}
      <div style={{ flex: 1, position: "relative", padding: "6px 8px", minHeight: 0 }}>
        <div ref={ref} style={{ width: "100%" }} />
        {empty && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#aaa",
              fontSize: 12,
            }}
          >
            no data yet — waiting for first 5s aggregation bin
          </div>
        )}
      </div>
    </div>
  );
}
